import type { Evaluation } from '@compass/client/types/evaluation';
import type { GetScorecardResponse, Scorecard } from '@compass/client/types/scorecard';
import { scorecardService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { QC_EVAL_DETAIL_QUERY_CONFIG } from '@/constants/queryConfig';
import { useStableFold } from '@/hooks/internal/useStableFold';

const foldScorecardsById = (queries: { data?: GetScorecardResponse }[]) => {
  const scorecardsById = new Map<string, Scorecard>();
  queries.forEach(query => {
    const scorecard = query.data?.scorecard;
    if (scorecard?.id) {
      scorecardsById.set(scorecard.id, scorecard);
    }
  });
  return scorecardsById;
};

// The scorecards behind the QC list's section columns (AQ-750): one fetch per distinct
// scorecard_id on the page, under the same key as useScorecard so the detail page shares it.
// isLoading is true while any of them has no data yet, so the caller can hold its columns.
export const useEvaluationScorecards = ({
  organizationId,
  evaluations,
  enabled,
}: {
  organizationId: string | null | undefined;
  evaluations: Evaluation[];
  enabled: boolean;
}): { scorecardsById: Map<string, Scorecard>; isLoading: boolean } => {
  const scorecardRefs = useMemo(() => {
    const refs = new Map<string, string>();
    if (enabled) {
      evaluations.forEach(({ program_id: programId, scorecard_id: scorecardId }) => {
        if (programId && scorecardId && !refs.has(scorecardId)) {
          refs.set(scorecardId, programId);
        }
      });
    }
    return Array.from(refs);
  }, [enabled, evaluations]);

  const scorecardQueries = useQueries({
    queries: scorecardRefs.map(([scorecardId, programId]) => ({
      queryKey: queryKeys.scorecards.byId(scorecardId, programId),
      queryFn: () =>
        scorecardService.getScorecard({
          organization_id: organizationId!,
          program_id: programId,
          scorecard_id: scorecardId,
        }),
      enabled: !!organizationId,
      staleTime: QC_EVAL_DETAIL_QUERY_CONFIG.staleTime,
    })),
  });

  const scorecardsById = useStableFold(
    scorecardQueries,
    query => query.data?.scorecard?.sections?.length ?? 0,
    foldScorecardsById,
  );
  // A failed fetch is not pending, so an error never holds the columns forever.
  const isLoading = scorecardQueries.some(query => query.isPending && query.fetchStatus !== 'idle');
  return { scorecardsById, isLoading };
};
